import { AreaTexto } from '@/shared/ui/Formulario/AreaTexto'
import { Campo } from '@/shared/ui/Formulario/Campo'
import { Selector } from '@/shared/ui/Formulario/Selector'

import type { CampoMaestro } from './tipos'
import estilos from './FormularioMaestro.module.css'

type PropsFormularioMaestro = {
  campos: CampoMaestro[]
  valores: Record<string, unknown>
  onCambiar: (nombre: string, valor: unknown) => void
  deshabilitado?: boolean
}

/** Tipo de entrada HTML que corresponde a cada tipo de campo declarado. */
const TIPO_HTML = {
  texto: 'text',
  numero: 'number',
  correo: 'email',
  telefono: 'tel',
  fecha: 'date',
} as const

/**
 * Dibuja el formulario de un maestro a partir de su declaración de campos.
 *
 * No sabe nada de clientes ni de herramientas: solo de tipos de campo. Toda la
 * particularidad de cada entidad vive en su archivo de definición.
 */
export function FormularioMaestro({
  campos,
  valores,
  onCambiar,
  deshabilitado = false,
}: PropsFormularioMaestro) {
  // Qué campos cuelgan de cada padre. Al cambiar un padre hay que limpiar a sus
  // hijos: si estaba «Antioquia · Medellín» y se pasa a «Chocó», Medellín no
  // puede quedar seleccionado. Se calcula una vez a partir de la declaración.
  const hijosDe: Record<string, string[]> = {}
  for (const campo of campos) {
    if (campo.dependeDe) (hijosDe[campo.dependeDe] ??= []).push(campo.nombre)
  }

  function cambiar(nombre: string, valor: unknown) {
    onCambiar(nombre, valor)
    for (const hijo of hijosDe[nombre] ?? []) onCambiar(hijo, null)
  }

  return (
    <div className={estilos.rejilla}>
      {campos.map((campo) => {
        const clase = campo.medio ? undefined : estilos.completo
        const valor = valores[campo.nombre]

        if (campo.tipo === 'area') {
          return (
            <div key={campo.nombre} className={clase}>
              <AreaTexto
                etiqueta={campo.etiqueta}
                ayuda={campo.ayuda}
                obligatorio={campo.obligatorio}
                placeholder={campo.marcador}
                disabled={deshabilitado}
                value={(valor as string) ?? ''}
                onChange={(e) => cambiar(campo.nombre, e.target.value)}
              />
            </div>
          )
        }

        if (campo.tipo === 'selector') {
          // En cascada las opciones salen del valor del padre; sin padre elegido
          // el desplegable va apagado, para que no se elija una ciudad sin antes
          // decir de qué departamento.
          const enCascada = campo.dependeDe != null
          const valorPadre = enCascada ? valores[campo.dependeDe!] : undefined
          const opciones = enCascada
            ? (campo.opcionesSegun?.(valorPadre) ?? [])
            : (campo.opciones ?? [])
          const apagado = deshabilitado || (enCascada && !valorPadre)

          return (
            <div key={campo.nombre} className={clase}>
              <Selector
                etiqueta={campo.etiqueta}
                ayuda={campo.ayuda}
                obligatorio={campo.obligatorio}
                opciones={opciones}
                disabled={apagado}
                value={(valor as string) ?? ''}
                // Un desplegable vacío no es cadena vacía en la base: es NULL.
                onChange={(e) => cambiar(campo.nombre, e.target.value || null)}
              />
            </div>
          )
        }

        return (
          <div key={campo.nombre} className={clase}>
            <Campo
              etiqueta={campo.etiqueta}
              ayuda={campo.ayuda}
              obligatorio={campo.obligatorio}
              placeholder={campo.marcador}
              numerico={campo.tipo === 'numero'}
              type={TIPO_HTML[campo.tipo]}
              min={campo.minimo}
              step={campo.paso}
              disabled={deshabilitado}
              value={(valor as string | number) ?? ''}
              onChange={(e) =>
                cambiar(
                  campo.nombre,
                  campo.tipo === 'numero'
                    ? // Un campo numérico vacío es NULL, no cero: cero es un
                      // dato, "sin dato" es otra cosa.
                      e.target.value === ''
                      ? null
                      : Number(e.target.value)
                    : e.target.value,
                )
              }
            />
          </div>
        )
      })}
    </div>
  )
}
